/**
 * One photo editor, reached from three doors.
 *
 * The product had two editors. `PhotoCaptureModalEnhanced` has the tabs, the area-selection tool
 * and face detection; `PhotoUploadModal` / `PhotoWebcamModal` / `PhotoIPCameraModal` each carried
 * an older one with sliders and a "Fundo" swatch. Which one an operator got depended on which
 * screen they started from, so the same vehicle photo looked and behaved differently depending on
 * the route in.
 *
 * The three old modals keep their own props and delegate to the one editor rather than ~15 call
 * sites being rewired. This is the translation between the two prop shapes — and it is where a
 * delegation refactor goes quietly wrong, so it is tested rather than inlined.
 */

export type PhotoModalSource = 'upload' | 'webcam' | 'ip_camera';

export interface LegacyPhotoModalProps {
  title?: string;
  cropShape?: 'round' | 'rect';
  aspectRatio?: number;
  /** The editor calls this `existingPhoto`. */
  initialImage?: string;
  disableFaceDetection?: boolean;
  customerId?: string;
  filterCategory?: 'captura_facial' | 'documento' | 'veiculo';
}

export interface EnhancedPhotoProps {
  initialTab: 0 | 1 | 2;
  title?: string;
  cropShape?: 'round' | 'rect';
  aspectRatio?: number;
  existingPhoto?: string;
  disableFaceDetection: boolean;
  customerId?: string;
  filterCategory?: 'captura_facial' | 'documento' | 'veiculo';
  hideIPCamera: boolean;
}

const TAB_FOR: Record<PhotoModalSource, 0 | 1 | 2> = {
  upload: 0,
  webcam: 1,
  ip_camera: 2,
};

export function enhancedPhotoProps(
  source: PhotoModalSource,
  legacy: LegacyPhotoModalProps,
): EnhancedPhotoProps {
  // IP cameras are registered per customer; without one the tab lists nothing and the operator is
  // staring at a dead end.
  const hideIPCamera = !legacy.customerId;

  return {
    // Never open on a tab that is not rendered.
    initialTab: hideIPCamera && TAB_FOR[source] === 2 ? 0 : TAB_FOR[source],
    title: legacy.title,
    cropShape: legacy.cropShape,
    aspectRatio: legacy.aspectRatio,
    existingPhoto: legacy.initialImage,
    // A car and a pet have no face; the old vehicle editor offered "Detectar Rosto" anyway.
    disableFaceDetection: !!legacy.disableFaceDetection,
    customerId: legacy.customerId,
    filterCategory: legacy.filterCategory,
    hideIPCamera,
  };
}
